import { type FC } from "react";
import type { Certificate } from "../../../shared/models/certificate";
import { formatDate, daysUntil } from "../lib/date_utils";
import { CopyButton } from "./copy_button";

const STATUS_BADGE: Record<
  Certificate["status"],
  { cls: string; label: string }
> = {
  active: { cls: "vmx-badge-success", label: "Active" },
  expiring: { cls: "vmx-badge-warning", label: "Expiring Soon" },
  expired: { cls: "vmx-badge-danger", label: "Expired" },
  revoked: { cls: "vmx-badge-neutral", label: "Revoked" },
};

export const CertificateDetailModal: FC<{
  cert: Certificate;
  groupName?: string;
  policyName?: string;
  onClose: () => void;
  onGroupClick?: () => void;
  onPolicyClick?: () => void;
  onRevoke?: () => void;
}> = ({
  cert,
  groupName,
  policyName,
  onClose,
  onGroupClick,
  onPolicyClick,
  onRevoke,
}) => {
  const badge = STATUS_BADGE[cert.status];
  const days = daysUntil(cert.validTo);
  const daysText =
    cert.status === "revoked"
      ? ""
      : days < 0
        ? `Expired ${Math.abs(days)} days ago`
        : days === 0
          ? "Expires today"
          : `${days} days remaining`;

  const detailRow = (label: string, value: string | React.ReactNode) => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 16,
        padding: "10px 0",
        borderBottom: "1px solid var(--vmx-gray-100)",
      }}
    >
      <span
        style={{
          fontSize: "0.8125rem",
          color: "var(--vmx-gray-500)",
          fontWeight: 500,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: "0.8125rem",
          color: "var(--vmx-gray-800)",
          fontWeight: 600,
        }}
      >
        {value}
      </span>
    </div>
  );

  return (
    <div className="vmx-modal" style={{ maxWidth: 480 }}>
      <div className="vmx-modal-header">
        <div className="vmx-modal-title">Certificate Details</div>
        <button
          className="vmx-btn vmx-btn-ghost vmx-btn-icon"
          onClick={onClose}
        >
          ✕
        </button>
      </div>
      <div className="vmx-modal-body">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            fontFamily: "var(--vmx-font-mono)",
            fontSize: "0.75rem",
            color: "var(--vmx-gray-400)",
            marginBottom: 16,
            wordBreak: "break-all",
          }}
        >
          <span>{cert.deviceId}</span>
          <CopyButton text={cert.deviceId} absolute={false} />
        </div>

        {detailRow(
          "Status",
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span className={`vmx-badge ${badge.cls}`}>{badge.label}</span>
            <span
              style={{
                fontSize: "0.75rem",
                color: "var(--vmx-gray-500)",
              }}
            >
              {daysText}
            </span>
          </div>
        )}
        {detailRow(
          "Device Group",
          groupName ? (
            <button
              type="button"
              onClick={onGroupClick}
              style={{
                color: "var(--vmx-accent-dark)",
                fontWeight: 600,
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                fontSize: "0.8125rem",
                textDecoration: "underline",
              }}
            >
              {groupName}
            </button>
          ) : (
            cert.deviceGroupId
          )
        )}
        {detailRow(
          "Certificate Policy",
          policyName ? (
            <button
              type="button"
              onClick={onPolicyClick}
              style={{
                color: "var(--vmx-accent-dark)",
                fontWeight: 600,
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                fontSize: "0.8125rem",
                textDecoration: "underline",
              }}
            >
              {policyName}
            </button>
          ) : (
            cert.certificatePolicyId
          )
        )}
        {detailRow("Valid From", formatDate(cert.validFrom))}
        {detailRow("Valid To", formatDate(cert.validTo))}
        {cert.revokedAt !== undefined &&
          detailRow("Revoked At", formatDate(cert.revokedAt))}
      </div>
      <div className="vmx-modal-footer">
        {(cert.status === "active" || cert.status === "expiring") && (
          <button className="vmx-btn vmx-btn-danger" onClick={onRevoke}>
            Revoke Certificate
          </button>
        )}
        <button className="vmx-btn vmx-btn-secondary" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
};
